import { useEffect, useState } from 'react';
import { autoPlacement, autoUpdate, offset, useDismiss, useFloating, useInteractions } from '@floating-ui/react';

import { useNavigationContext } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContext';
import { logError } from 'services/logging/logging';
import { WebEDIAnnotation } from 'services/webediErrors/types';
import { GenericError } from 'support/helpers/errors/errors';
import { Alert } from 'components/Display/Alert/Alert';
import { Translate } from 'components/Translate/Translate';

import { getBannerSeverity, getBannerTitle } from './helpers';

type MessageNonFieldErrorBannerProps = {
  annotation: WebEDIAnnotation;
  sectionDescription: string | undefined;
  totalNumberOfErrors: number;
  setCurrentItem: (annotation: WebEDIAnnotation) => void;
};

export const MessageNonFieldErrorBanner = ({
  annotation,
  sectionDescription,
  totalNumberOfErrors,
  setCurrentItem,
}: MessageNonFieldErrorBannerProps) => {
  const [showNavigableItemPopover, setShowNavigableItemPopover] = useState(false);
  const { NavigableItemPopoverComponent } = useNavigationContext();
  const { refs, floatingStyles, context } = useFloating({
    open: showNavigableItemPopover,
    onOpenChange: setShowNavigableItemPopover,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(8),
      autoPlacement({ allowedPlacements: ['bottom-start', 'bottom', 'bottom-end', 'top-start', 'top', 'top-end'] }),
    ],
  });
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

  useEffect(() => {
    if (totalNumberOfErrors === 0 && annotation) {
      logError(new GenericError('MessageNonFieldErrorBanner: No errors found when rendering the banner', annotation));
    }
  }, [annotation, totalNumberOfErrors]);

  useEffect(() => {
    if (annotation && showNavigableItemPopover) {
      setCurrentItem(annotation);
    }
  }, [annotation, setCurrentItem, showNavigableItemPopover]);

  if (totalNumberOfErrors === 0) {
    return null;
  }

  const severity = getBannerSeverity(annotation);
  const title = getBannerTitle(annotation);

  return (
    <>
      <Alert
        severity={severity}
        actions={{
          alignment: 'right',
          primary: {
            variant: severity === 'warning' ? 'minimal' : 'minimal-danger',
            children: <Translate i18nKey="webedi:MessageNonFieldErrorBanner.cta" />,
            onFocus: () => setShowNavigableItemPopover(true),
            onBlur: () => setShowNavigableItemPopover(false),
            size: 'extra-small',
            ref: refs.setReference,
            ...getReferenceProps(),
            'data-field': annotation.fieldPath,
            onClick: () => setShowNavigableItemPopover(true),
          } as any,
        }}
      >
        {title.kind === 'key' ? <Translate i18nKey={title.value} /> : title.value}
      </Alert>

      {showNavigableItemPopover && NavigableItemPopoverComponent ? (
        <div {...getFloatingProps()}>
          <NavigableItemPopoverComponent
            viewMode={false}
            annotation={annotation}
            ref={refs.setFloating}
            style={floatingStyles}
            fieldDescription={sectionDescription}
          />
        </div>
      ) : null}
    </>
  );
};
